import type { Permissions } from '@wix/wix-data-schema-types'
import { PermissionRole } from '@wix/wix-data-schema-types'
import { match } from 'ts-pattern'

export type PermissionsInfoText = {
  translationKey: string
  type: 'standard' | 'alert'
}

type Properties = {
  collectionPermissions: Permissions
  hasInputBindings: boolean
  hasDisplayElementBindings: boolean
  hasUserInputFilters: boolean
  allInputBindingsAreUserInputFilters: boolean
  isDynamicPage: boolean
  isRouterDataset: boolean
  isMembersOnlyPage: boolean
  isConnectedToWixAppCollection: boolean
}

export const derivePermissionsInfoText = (
  properties: Properties,
): PermissionsInfoText | undefined => {
  return match(properties)
    .with(
      {
        hasInputBindings: true,
        hasUserInputFilters: false,
        isConnectedToWixAppCollection: true,
      },
      () => ({
        translationKey: 'wixStudio.permissions.input.wixAppCollection',
        type: 'alert' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasDisplayElementBindings: true,
        collectionPermissions: {
          insert: PermissionRole.admin,
          read: PermissionRole.admin,
        },
      },
      () => ({
        translationKey: 'wixStudio.advancedPermissions.input.display.admin',
        type: 'alert' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        allInputBindingsAreUserInputFilters: false,
        isDynamicPage: false,
        collectionPermissions: { insert: PermissionRole.admin },
      },
      () => ({
        translationKey: 'wixStudio.advancedPermissions.input.static.admin.add',
        type: 'alert' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasUserInputFilters: false,
        allInputBindingsAreUserInputFilters: false,
        isDynamicPage: true,
        isRouterDataset: true,
        collectionPermissions: { update: PermissionRole.admin },
      },
      () => ({
        translationKey:
          'wixStudio.advancedPermissions.input.dynamic.admin.update',
        type: 'alert' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasUserInputFilters: true,
        hasDisplayElementBindings: false,
        collectionPermissions: { read: PermissionRole.admin },
      },
      {
        hasInputBindings: false,
        hasUserInputFilters: false,
        hasDisplayElementBindings: true,
        collectionPermissions: { read: PermissionRole.admin },
      },
      () => ({
        translationKey: 'wixStudio.advancedPermissions.inputFilter.admin.view',
        type: 'alert' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasDisplayElementBindings: true,
        collectionPermissions: {
          insert: PermissionRole.siteMember,
          read: PermissionRole.siteMember,
        },
      },
      () => ({
        translationKey:
          'wixStudio.advancedPermissions.input.display.siteMember',
        type: 'standard' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasUserInputFilters: false,
        isDynamicPage: false,
        collectionPermissions: { insert: PermissionRole.siteMember },
        isMembersOnlyPage: false,
      },
      () => ({
        translationKey:
          'wixStudio.advancedPermissions.input.static.siteMember.add',
        type: 'standard' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasUserInputFilters: false,
        allInputBindingsAreUserInputFilters: false,
        isDynamicPage: true,
        isRouterDataset: true,
        collectionPermissions: { update: PermissionRole.siteMember },
        isMembersOnlyPage: false,
      },
      () => ({
        translationKey:
          'wixStudio.advancedPermissions.input.dynamic.siteMember.update',
        type: 'standard' as const,
      }),
    )
    .with(
      {
        hasInputBindings: true,
        hasUserInputFilters: true,
        collectionPermissions: { read: PermissionRole.siteMember },
      },
      {
        hasInputBindings: false,
        hasUserInputFilters: false,
        collectionPermissions: { read: PermissionRole.siteMember },
      },
      () => ({
        translationKey:
          'wixStudio.advancedPermissions.inputFilter.siteMember.view',
        type: 'standard' as const,
      }),
    )
    .otherwise(() => undefined)
}
